import React, { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PasswordValidator from 'password-validator';
import axios from 'axios';
import { Pathname } from 'Routes';
import { dataFetcher, endpoints, baseUrl } from 'Api';
import { AuthContext, AppContext } from 'Context';
import { Button, Snackbar } from 'Components';
import RotatingLoader from 'Components/RotatingLoader';
import { Input } from '../components/input-field';
import { AuthPage } from '../components/page';
import authStyles from '../auth.module.css';
import styles from './index.module.css';

const passwordSchema = new PasswordValidator()
  .is()
  .min(12)
  .is()
  .max(30)
  .has()
  .symbols(1)
  .has()
  .digits(1)
  .has()
  .uppercase(1)
  .lowercase(1);
const defaultSnackbar = {
  isVisible: false,
  type: undefined,
  message: undefined,
};

export const SetPassword = () => {
  const navigate = useNavigate();
  const confirmPasswordRef = useRef(null);
  const [searchParams] = useSearchParams();
  const { setToken, forceAutoSignin } = useContext(AuthContext);
  const { setAppSnackbar } = useContext(AppContext);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [snackbar, setSnackbar] = useState(defaultSnackbar);
  const [temporaryToken, setTemporaryToken] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [noPassword, setNoPassword] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Extract token from URL and verify with API
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setTemporaryToken(token);
      // console.log('Token extracted from URL:', token);

      // Call signinEmail API to check if user needs to set password
      // Using direct axios call to avoid interceptor overwriting the token
      setIsLoading(true);
      axios
        .post(`${baseUrl}${endpoints.signinEmail}`, { token })
        .then(({ data }) => {
          // console.log('========== signinEmail API Response ==========');
          // console.log('Full Response:', data);
          // console.log('Success:', data.success);
          // console.log('No Password:', data.no_password);
          // console.log('Message:', data.message);
          // console.log('==============================================');

          if (data.success && data.no_password !== undefined) {
            setNoPassword(data.no_password);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          // console.error('========== signinEmail API Error ==========');
          // console.error('Error:', error);
          // console.error('Error Response:', error.response?.data);
          // console.error('===========================================');
          setIsLoading(false);
          setSnackbar({
            isVisible: true,
            type: 'error',
            message: 'Invalid or expired token',
          });
        });
    } else {
      // console.log('No token found in URL');
      setIsLoading(false);
    }
  }, [searchParams]);

  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const isPasswordValid = passwordSchema.validate(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const passwordHealth = (
    password
      ? password.length < 12 || !hasUpperCase
        ? 'weak'
        : passwordSchema.validate(password)
          ? password.length >= 12
            ? 'strong'
            : 'medium'
          : 'weak'
      : ''
  );

  const handleNext = async (e) => {
    e.preventDefault();

    if (!password) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Please enter a password',
      });
      return;
    }

    if (!isPasswordValid) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Password does not meet requirements',
      });
      return;
    }

    if (password.length > 30) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Password cannot exceed 30 characters',
      });
      return;
    }

    if (!passwordsMatch) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Passwords do not match',
      });
      return;
    }

    // Call setPassword API
    // Using direct axios call to avoid interceptor overwriting the token
    setSubmitLoading(true);
    try {
      const payload = {
        token: temporaryToken,
        password,
      };

      // console.log('========== Set Password API Request ==========');
      // console.log('Payload:', payload);
      // console.log('==============================================');

      const { data } = await axios.post(`${baseUrl}${endpoints.setPassword}`, payload);

      // console.log('========== Set Password API Response ==========');
      // console.log('Full Response:', data);
      // console.log('Success:', data.success);
      // console.log('Token:', data.data?.token);
      // console.log('===============================================');

      if (data.success && data.data?.token) {
        // Store token in localStorage
        setToken(data.data.token);

        // Show success message (matching login/signup flow)
        setAppSnackbar({
          message: 'Password set successfully',
          isVisible: true,
        });

        // Check if new user from API response (matching social login logic)
        const isNewUser = data.new_user || data.data?.is_new_user;

        // Wait a moment, then trigger auto sign-in and navigate
        setTimeout(() => {
          forceAutoSignin();

          // Navigate to avatar for new users (matching social login flow)
          // Navigate to access for existing users
          if (isNewUser) {
            navigate(Pathname.authentication.avatar);
          } else {
            // Force page reload to ensure proper authentication state
            window.location.href = '/access';
          }
        }, 500);
      } else {
        setSnackbar({
          isVisible: true,
          type: 'error',
          message: data.message || 'Failed to set password',
        });
        setSubmitLoading(false);
      }
    } catch (error) {
      // console.error('========== Set Password API Error ==========');
      // console.error('Error:', error);
      // console.error('Error Response:', error.response?.data);
      // console.error('============================================');

      setSnackbar({
        isVisible: true,
        type: 'error',
        message: error.response?.data?.message || (typeof error.message === 'string' ? error.message : 'An error occurred while setting password'),
      });
      setSubmitLoading(false);
    }
  };

  const handlePasswordInput = (event) => {
    const inputPassword = event.target.value;
    if (inputPassword.length > 30) {
      setPassword(inputPassword.slice(0, 30));
    } else {
      setPassword(inputPassword);
    }
  };

  // Show loader while checking token
  if (isLoading) {
    return (
      <AuthPage>
        <div className={`${authStyles.authPanel} ${styles.contentWrapper}`} style={{ marginTop: '-20px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <svg className="loading-circle" viewBox="0 0 50 50" style={{ width: '50px', height: '50px' }}>
              <circle
                className="circle"
                cx="25"
                cy="25"
                r="20"
                fill="none"
                strokeWidth="4"
              />
            </svg>
          </div>
        </div>
      </AuthPage>
    );
  }

  // Show message if user already has a password
  if (noPassword === false) {
    return (
      <AuthPage>
        <Snackbar {...snackbar} onClose={() => setSnackbar(defaultSnackbar)} />
        <div className={`${authStyles.authPanel} ${styles.contentWrapper}`} style={{ marginTop: '-20px' }}>
          <div className={styles.header} style={{ marginBottom: '20px', textAlign: 'center' }}>
            <h1
              style={{
                fontSize: '24px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#ffffff',
              }}
            >
              Password Already Set
            </h1>
            <p
              style={{
                fontSize: '14px',
                color: '#b0b0b0',
                lineHeight: '1.4',
                margin: '0 0 24px 0',
              }}
            >
              You already have a password. Please login with your existing credentials.
            </p>
            <Button
              blockText
              isBlue
              isFullWidth
              isLarge
              onClick={() => navigate('/newSign-in')}
            >
              Go to Login
            </Button>
          </div>
        </div>
      </AuthPage>
    );
  }

  // Show set password form if no_password is true
  return (
    <AuthPage>
      {submitLoading && <RotatingLoader dark />}
      <Snackbar {...snackbar} onClose={() => setSnackbar(defaultSnackbar)} />
      <div className={`${authStyles.authPanel} ${styles.contentWrapper}`} style={{ marginTop: '-20px' }}>
        <div className={styles.header} style={{ marginBottom: '20px', textAlign: 'center' }}>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: '600',
              marginBottom: '8px',
              color: '#ffffff',
            }}
          >
            Create Password
          </h1>
          <p
            style={{
              fontSize: '14px',
              color: '#b0b0b0',
              lineHeight: '1.4',
              margin: '0',
            }}
          >
            Create a secure password for your account
          </p>
        </div>

        <form onSubmit={handleNext}>
          <div className={styles.formField} style={{ marginBottom: '16px' }}>
            <Input
              label="Create Password"
              type="password"
              automaticFocus
              value={password}
              onInput={handlePasswordInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  confirmPasswordRef.current?.focus();
                }
              }}
              onPaste={(e) => {
                const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                if (pastedText.length > 30) {
                  e.preventDefault();
                }
                const totalLength = e.target.value.length + pastedText.length;
                if (totalLength > 30) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const inputPassword = e.target.value;
                if (inputPassword.length > 30) {
                  const trimmedText = inputPassword.slice(0, 30);
                  e.target.value = trimmedText;
                  setPassword(trimmedText);
                }
              }}
              placeholder="Enter password"
              minLength={12}
              maxLength={30}
              required
            />
          </div>

          <div className={styles.formField} style={{ marginBottom: '16px' }}>
            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              required
              inputRef={confirmPasswordRef}
              helperText={
                confirmPassword
                && !passwordsMatch && (
                  <span className={authStyles.errorText}>
                    Passwords do not match
                  </span>
                )
              }
            />
          </div>

          <div
            className={styles.formField}
            style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: 'rgba(0, 117, 255, 0.1)',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#b0b0b0',
            }}
          >
            <p
              style={{
                margin: '0 0 6px 0',
                fontWeight: '600',
                color: '#ffffff',
                fontSize: '13px',
                textAlign: 'left',
              }}
            >
              Password Requirements:
            </p>
            <ul
              style={{
                margin: '0',
                paddingLeft: '18px',
                lineHeight: '1.6',
                textAlign: 'left',
              }}
            >
              <li style={{ color: password && password.length >= 12 && password.length <= 30 ? '#23e223' : '#b0b0b0' }}>12 - 30 Characters</li>
              <li style={{ color: password && hasUpperCase ? '#23e223' : '#b0b0b0' }}>Minimum of one uppercase</li>
              <li style={{ color: password && password.match(/[a-z]/) ? '#23e223' : '#b0b0b0' }}>Minimum of one lowercase</li>
              <li style={{ color: password && password.match(/[0-9]/) ? '#23e223' : '#b0b0b0' }}>Minimum of one number</li>
              <li style={{ color: password && password.match(/[!@#$%^&*()_+|~=`{}[\]:";'<>?,./]/) ? '#23e223' : '#b0b0b0' }}>Minimum of one special character</li>
            </ul>
          </div>

          <Button
            blockText
            isBlue
            isFullWidth
            isLarge
            submit
            isDisabled={
              submitLoading
              || !password
              || !confirmPassword
              || !passwordsMatch
              || !((passwordHealth === 'strong') || (passwordHealth === 'medium'))
            }
          >
            {submitLoading ? 'Updating your Profile...' : 'Next'}
          </Button>
        </form>
      </div>
    </AuthPage>
  );
};
